import BaseModal from 'components/atoms/base-modal';
import { useManualClinicNotesLog } from 'infrastructure/hooks/logs/use-manual-clinic-notes-log';
import { useAppSelector } from 'store';
import { currentMonthRange } from 'infrastructure/consts/current-month-range';
import { ModuleType } from 'infrastructure/enums';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import BaseForm from 'components/atoms/base-form';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useEffect, type FC } from 'react';
import { isCombinedModule, isRpmModule } from 'infrastructure/functions';
import classNames from 'classnames';
import i18next from 'i18next';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useModule } from 'infrastructure/providers/module-provider';

import { manualInteractiveTimeSchema, type Values } from './validation-schema';
import s from './style.module.scss';
import ModuleTabs from '../module-tabs';

import type { FormikHelpers } from 'formik';
import type {
  DataProps,
  TSetManualInteractionTime,
} from 'components/organisms/interactiveTime/hooks';

interface ManualInteractionTimeModalNewProps {
  isOpen: boolean;
  toggle: () => void;
  patientGuid: string;
  module: ModuleType;
  loading: boolean;
  submit: TSetManualInteractionTime;
  disableCcm?: boolean;
  disableRpm?: boolean;
}

const ManualInteractionTimeModalNew: FC<ManualInteractionTimeModalNewProps> = ({
  isOpen,
  toggle,
  patientGuid,
  module,
  loading,
  submit,
  disableCcm,
  disableRpm,
}) => {
  const { module: moduleType, changeModule: setModuleType } = useModule();

  const dateRange = useAppSelector<[Date, Date]>((state) => state.sms.dates);
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();
  const { refetch } = useManualClinicNotesLog({
    patientGuid,
    meta: {
      dateRange: dateRange.length ? dateRange : currentMonthRange,
      module: ModuleType.RPM,
    },
  });

  const onSubmit = async (
    { time, comment }: Values,
    formikHelpers: FormikHelpers<Values>,
  ) => {
    const confirm = await showAlert({
      messageTitle: i18next.t('confirm.confirmAddManualTime'),
      title: i18next.t('confirm.title'),
      rejectBtnLabel: t('controls.no'),
      successBtnLabel: t('controls.yes'),
    });

    if (confirm && time && comment) {
      const postData: DataProps = {
        patientGuid,
        isRpm: isRpmModule(moduleType),
        timeInMinutes: time,
        comment,
      };
      submit(postData, {
        onSuccess: () => {
          formikHelpers.resetForm();
          refetch();
        },
      });
    }
  };

  const formik = useFormik<Values>({
    initialValues: manualInteractiveTimeSchema().cast({}),
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: manualInteractiveTimeSchema(),
  });

  const { submitForm, resetForm, values } = formik;

  const onCancel = async () => {
    if (values.comment) {
      const confirmCancel = await showAlert({
        messageTitle: i18next.t('confirm.closeClinicalNotePopup'),
        title: i18next.t('confirm.cancelManualTime'),
        rejectBtnLabel: t('controls.no'),
        successBtnLabel: t('controls.yes'),
      });

      if (confirmCancel) {
        resetForm();
        toggle();
      }
    } else {
      resetForm();
      toggle();
    }
  };

  useEffect(() => {
    if (disableRpm) setModuleType(ModuleType.CCM);
    if (disableCcm) setModuleType(ModuleType.RPM);
  }, [disableCcm, disableRpm]);

  return (
    <>
      <BaseModal
        open={isOpen}
        onCancel={onCancel}
        title={t('labels.addManualTime')}
        bodyClassNames={s.body}
        withFooter={[
          <BaseButton
            dataCy="cancel-button"
            key="cancel"
            type="secondary"
            label={t('controls.cancel')}
            onClick={onCancel}
          />,
          <BaseButton
            dataCy="add-time-button"
            key="add-time-button"
            type="primary"
            label={t('controls.addTime')}
            loading={loading}
            onClick={submitForm}
          />,
        ]}
      >
        <BaseForm formik={formik}>
          <div
            className={classNames(s['form-wrapper'], {
              [s['without-switcher']]: !isCombinedModule(module),
            })}
          >
            <BaseFormItem
              name="time"
              label={`${t('labels.time')} [min]`}
              wrapperClassName={s.time}
              isBlackLabel
              required
            >
              <BaseInput />
            </BaseFormItem>
            {isCombinedModule(module) && (
              <ModuleTabs
                className={s.tabs}
                activeKey={moduleType}
                rpmDisabled={disableRpm}
                ccmDisabled={disableCcm}
                onChange={(tab) => setModuleType(tab.key)}
              />
            )}
            <BaseFormItem
              name="comment"
              wrapperClassName={s.comment}
              label={t('labels.comment')}
              isBlackLabel
              required
            >
              <BaseTextArea rows={10} placeholder="Type here your comment" />
            </BaseFormItem>
          </div>
        </BaseForm>
      </BaseModal>
      {Alert}
    </>
  );
};
export default ManualInteractionTimeModalNew;
